import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  FiShoppingBag,
  FiEye,
  FiDownload,
  FiFilter,
  FiSearch,
  FiChevronDown,
  FiStar,
  FiMessageCircle,
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiRefreshCw,
} from "react-icons/fi";
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaWhatsapp,
  FaTelegram,
  FaTiktok,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import { fetchOrders } from "../store/slices/paymentSlice";
import { format } from "date-fns";
import { GlassCard } from "../components/ui/GlassCard";
import { LoadingSkeleton } from "../components/ui/LoadingSkeleton";
import toast from "react-hot-toast";

export const OrdersPage = () => {
  const dispatch = useDispatch();
  const { orders, isLoading } = useSelector((state) => state.payments);
  const accountOrders = Array.isArray(orders)
    ? orders.filter((order) => order.account && order.type !== "virtual_number")
    : [];
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  useEffect(() => {
    if (accountOrders) {
      let filtered = [...accountOrders];

      // Apply search
      if (searchTerm) {
        filtered = filtered.filter(
          (o) =>
            o.account?.title
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            o.account?.socialMediaType
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()),
        );
      }

      // Apply status filter
      if (statusFilter !== "all") {
        filtered = filtered.filter((o) => o.status === statusFilter);
      }

      // Apply sorting
      filtered.sort((a, b) => {
        switch (sortBy) {
          case "newest":
            return new Date(b.createdAt) - new Date(a.createdAt);
          case "oldest":
            return new Date(a.createdAt) - new Date(b.createdAt);
          case "amount_high":
            return b.amount - a.amount;
          case "amount_low":
            return a.amount - b.amount;
          default:
            return 0;
        }
      });

      setFilteredOrders(filtered);
    }
  }, [accountOrders, searchTerm, statusFilter, sortBy]);

  const platformIcons = {
    Facebook: FaFacebook,
    Instagram: FaInstagram,
    Twitter: FaTwitter,
    WhatsApp: FaWhatsapp,
    Telegram: FaTelegram,
    TikTok: FaTiktok,
  };

  const platformColors = {
    Facebook: "from-blue-600 to-blue-400",
    Instagram: "from-purple-600 to-pink-500",
    Twitter: "from-sky-400 to-blue-500",
    WhatsApp: "from-green-500 to-emerald-400",
    Telegram: "from-blue-400 to-cyan-400",
    TikTok: "from-gray-800 to-gray-600",
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "failed":
        return "bg-red-100 text-red-700";
      case "refunded":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-luxury-100 text-luxury-700";
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getOrderCredentials = (order) => {
    if (order.accountDetails && Object.keys(order.accountDetails).length > 0) {
      return order.accountDetails;
    }

    const details = order.account?.details;
    if (!details?.length) return null;

    const usedDetails = details.filter((detail) => detail.used);
    if (!usedDetails.length) return null;

    const orderDate = new Date(
      order.completedAt || order.createdAt || Date.now(),
    );
    const closestDetail = usedDetails.reduce((closest, detail) => {
      if (!closest) return detail;
      const currentDiff = Math.abs(
        new Date(detail.usedAt || orderDate) - orderDate,
      );
      const closestDiff = Math.abs(
        new Date(closest.usedAt || orderDate) - orderDate,
      );
      return currentDiff < closestDiff ? detail : closest;
    }, null);

    return closestDetail?.data || null;
  };

  const downloadCredentials = (order) => {
    const credentials = getOrderCredentials(order) || {};
    const content = `
      Account Credentials
      ==================
      Platform: ${order.account?.socialMediaType}
      Account: ${order.account?.title}
      ${Object.entries(credentials)
        .map(
          ([key, value]) =>
            `${key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")}: ${value}`,
        )
        .join("\n")}
      Purchase Date: ${format(new Date(order.completedAt), "MMMM dd, yyyy HH:mm")}
    `.trim();

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `account_${order._id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Credentials downloaded!");
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const selectedOrderCredentials = selectedOrder
    ? getOrderCredentials(selectedOrder)
    : null;

  return (
    <>
      <Helmet>
        <title>My Orders | Nitro Logs</title>
      </Helmet>

      <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-luxury-50 via-white to-luxury-100">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="mb-8"
          >
            <h1 className="text-3xl font-display font-bold text-luxury-900 mb-2">
              My <span className="gradient-text">Orders</span>
            </h1>
            <p className="text-luxury-600">
              View and manage all your purchased accounts
            </p>
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-8"
          >
            <motion.div variants={fadeInUp}>
              <GlassCard className="p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2">
                  <FiShoppingBag className="w-4 sm:w-5 h-4 sm:h-5 text-primary-500" />
                  <span className="text-xs text-luxury-500">Total Orders</span>
                </div>
                <span className="text-lg sm:text-xl font-bold gradient-text">
                  {accountOrders.length}
                </span>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <FiCheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-xs text-luxury-500">Completed</span>
                </div>
                <span className="text-xl font-bold gradient-text">
                  {accountOrders.filter((o) => o.status === "completed").length}
                </span>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <FiClock className="w-5 h-5 text-yellow-500" />
                  <span className="text-xs text-luxury-500">Pending</span>
                </div>
                <span className="text-xl font-bold gradient-text">
                  {accountOrders.filter((o) => o.status === "pending").length}
                </span>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <FiRefreshCw className="w-5 h-5 text-orange-500" />
                  <span className="text-xs text-luxury-500">Total Spent</span>
                </div>
                <span className="text-xl font-bold gradient-text">
                  ₦
                  {accountOrders
                    .reduce((sum, o) => sum + (o.amount || 0), 0)
                    .toLocaleString()}
                </span>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Filters Bar */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="glass rounded-2xl p-3 sm:p-4 mb-8 w-full"
          >
            <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 w-full">
              {/* Search */}
              <div className="flex-1 relative min-w-0">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400" />
                <input
                  type="text"
                  placeholder="Search by account name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-luxury pl-12 w-full text-sm"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 sm:px-6 py-3 rounded-xl flex items-center space-x-2 transition-all duration-300 whitespace-nowrap ${
                  showFilters
                    ? "bg-primary-600 text-white"
                    : "glass hover:bg-primary-50 text-luxury-700"
                }`}
              >
                <FiFilter className="w-4 sm:w-5 h-4 sm:h-5" />
                <span className="hidden sm:inline">Filters</span>
                <FiChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${
                    showFilters ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>

            {/* Filter Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-4 sm:pt-6 mt-4 border-t border-luxury-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {/* Status Filter */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Status
                        </label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="all">All Status</option>
                          <option value="completed">Completed</option>
                          <option value="pending">Pending</option>
                          <option value="failed">Failed</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>

                      {/* Sort By */}
                      <div>
                        <label className="block text-sm font-medium text-luxury-700 mb-2">
                          Sort By
                        </label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="input-luxury"
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="amount_high">
                            Amount: High to Low
                          </option>
                          <option value="amount_low">
                            Amount: Low to High
                          </option>
                        </select>
                      </div>
                    </div>

                    {/* Active Filters */}
                    <div className="flex flex-wrap gap-2 mt-3 sm:mt-4">
                      {statusFilter !== "all" && (
                        <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm flex items-center">
                          Status: {statusFilter}
                          <button
                            onClick={() => setStatusFilter("all")}
                            className="ml-2 hover:text-primary-900"
                          >
                            ×
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Orders List */}
          {isLoading ? (
            <LoadingSkeleton count={5} />
          ) : (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="space-y-4"
            >
              {filteredOrders.length > 0 ? (
                filteredOrders.map((order) => {
                  const Icon =
                    platformIcons[order.account?.socialMediaType] || FaFacebook;
                  const color =
                    platformColors[order.account?.socialMediaType] ||
                    "from-primary-500 to-secondary-500";

                  return (
                    <motion.div
                      key={order._id}
                      variants={fadeInUp}
                      whileHover={{ scale: 1.01 }}
                      className="glass rounded-2xl p-3 sm:p-4 cursor-pointer hover:shadow-luxury-lg transition-all duration-300 overflow-hidden"
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowDetails(true);
                      }}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div
                            className={`w-10 sm:w-12 h-10 sm:h-12 rounded-xl bg-gradient-to-r ${color} p-2 sm:p-3 flex items-center justify-center flex-shrink-0`}
                          >
                            <Icon className="w-5 sm:w-6 h-5 sm:h-6 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center space-x-2 flex-wrap">
                              <h3 className="font-semibold text-luxury-900 truncate">
                                {order.account?.title}
                              </h3>
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(order.status)}`}
                              >
                                {order.status}
                              </span>
                            </div>
                            <p className="text-sm text-luxury-500 truncate">
                              {order.account?.socialMediaType} •{" "}
                              {order.account?.followers?.toLocaleString()}{" "}
                              followers
                            </p>
                            <p className="text-xs text-luxury-400">
                              {format(
                                new Date(order.completedAt || order.createdAt),
                                "MMM dd, yyyy",
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg sm:text-xl font-bold gradient-text">
                            ₦{order.amount?.toLocaleString()}
                          </p>
                          <div className="flex items-center space-x-1 sm:space-x-2 mt-2 justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrder(order);
                                setShowCredentials(true);
                              }}
                              className="p-2 glass rounded-lg hover:bg-primary-50 transition-colors"
                              title="View Credentials"
                            >
                              <FiEye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadCredentials(order);
                              }}
                              className="p-2 glass rounded-lg hover:bg-primary-50 transition-colors"
                              title="Download Credentials"
                            >
                              <FiDownload className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              ) : (
                <motion.div
                  variants={fadeInUp}
                  className="text-center py-12 sm:py-20"
                >
                  <div className="w-16 sm:w-24 h-16 sm:h-24 mx-auto mb-4 sm:mb-6 bg-luxury-200 rounded-full flex items-center justify-center">
                    <FiShoppingBag className="w-6 sm:w-8 h-6 sm:h-8 text-luxury-400" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-luxury-900 mb-2">
                    No Orders Yet
                  </h3>
                  <p className="text-sm sm:text-base text-luxury-600 mb-6">
                    Start browsing our collection and make your first purchase
                  </p>
                  <Link to="/accounts">
                    <button className="luxury-button">Browse Accounts</button>
                  </Link>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* Order Details Modal */}
          <AnimatePresence>
            {showDetails && selectedOrder && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setShowDetails(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass max-w-2xl w-full rounded-2xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 className="text-lg sm:text-xl font-semibold text-luxury-900 mb-4">
                    Order Details
                  </h2>

                  <div className="space-y-4 w-full">
                    {/* Account Info */}
                    <div className="flex items-center space-x-3 p-3 sm:p-4 bg-luxury-100 rounded-xl">
                      <div
                        className={`w-12 h-12 rounded-xl bg-linear-to-r ${
                          platformColors[
                            selectedOrder.account?.socialMediaType
                          ] || "from-primary-500 to-secondary-500"
                        } p-3`}
                      >
                        {React.createElement(
                          platformIcons[
                            selectedOrder.account?.socialMediaType
                          ] || FaFacebook,
                          {
                            className: "w-6 h-6 text-white",
                          },
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-luxury-900">
                          {selectedOrder.account?.title}
                        </h3>
                        <p className="text-sm text-luxury-500">
                          {selectedOrder.account?.socialMediaType}
                        </p>
                      </div>
                    </div>

                    {/* Order Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <p className="text-sm text-luxury-900">Order ID</p>
                        <p className="font-mono text-sm text-luxury-900">
                          {selectedOrder._id}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-luxury-900">Status</p>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedOrder.status)}`}
                        >
                          {selectedOrder.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-luxury-900">Amount</p>
                        <p className="font-bold gradient-text">
                          ₦{selectedOrder.amount?.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-luxury-900">Purchase Date</p>
                        <p className="text-luxury-900">
                          {format(
                            new Date(
                              selectedOrder.completedAt ||
                                selectedOrder.createdAt,
                            ),
                            "MMM dd, yyyy",
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Credentials Preview */}
                    {selectedOrderCredentials && (
                      <div className="pt-4 border-t border-luxury-200">
                        <h3 className="font-semibold text-luxury-900 mb-3">
                          Account Credentials
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(selectedOrderCredentials)
                            .slice(0, 2)
                            .map(([key, value]) => (
                              <div
                                key={key}
                                className="flex justify-between items-center p-2 bg-luxury-100 rounded-lg"
                              >
                                <span className="text-sm text-luxury-600 capitalize">
                                  {key.replace(/([A-Z])/g, " $1").trim()}:
                                </span>
                                <span className="text-sm font-mono">
                                  {typeof value === "string" &&
                                  value.length > 20
                                    ? "••••••••"
                                    : String(value).substring(0, 20)}
                                </span>
                              </div>
                            ))}
                          {Object.keys(selectedOrderCredentials).length > 2 && (
                            <p className="text-xs text-luxury-500 text-center py-2">
                              +
                              {Object.keys(selectedOrderCredentials).length - 2}{" "}
                              more fields
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-luxury-200">
                      <button
                        onClick={() => {
                          setShowDetails(false);
                          setShowCredentials(true);
                        }}
                        className="flex-1 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                      >
                        View Full Credentials
                      </button>
                      <button
                        onClick={() => downloadCredentials(selectedOrder)}
                        className="flex items-center justify-center gap-2 px-4 py-2 glass rounded-xl hover:bg-primary-50 transition-colors"
                      >
                        <FiDownload className="w-5 h-5" />
                        <span>Download Details</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Credentials Modal */}
          <AnimatePresence>
            {showCredentials && selectedOrder && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setShowCredentials(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="glass max-w-md w-full rounded-2xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 className="text-lg sm:text-xl font-semibold text-luxury-900 mb-4">
                    Account Credentials
                  </h2>

                  {selectedOrderCredentials && (
                    <div className="space-y-4">
                      {/* <div className="p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                        <div className="flex items-start space-x-2">
                          <FiAlertCircle className="w-4 sm:w-5 h-4 sm:h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs sm:text-sm text-yellow-700">
                            Keep these credentials safe. Change the password immediately after login.
                          </p>
                        </div>
                      </div> */}

                      <div className="space-y-3 w-full">
                        {Object.entries(selectedOrderCredentials).map(
                          ([key, value]) => (
                            <div key={key}>
                              <label className="block text-xs sm:text-sm text-luxury-500 mb-1 capitalize">
                                {key.replace(/([A-Z])/g, " $1").trim()}
                              </label>
                              <div className="flex items-center justify-between gap-2 p-2 sm:p-3 bg-luxury-100 rounded-lg overflow-hidden">
                                {typeof value === "string" &&
                                (value.includes("http") ||
                                  value.includes("www")) ? (
                                  <a
                                    href={value}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:underline text-xs sm:text-sm truncate"
                                  >
                                    {value}
                                  </a>
                                ) : (
                                  <span className="font-mono text-xs sm:text-sm truncate">
                                    {String(value)}
                                  </span>
                                )}
                                <button
                                  onClick={() => copyToClipboard(String(value))}
                                  className="text-primary-600 hover:text-primary-700 flex-shrink-0"
                                >
                                  Copy
                                </button>
                              </div>
                            </div>
                          ),
                        )}
                      </div>

                      <div className="pt-4 border-t border-luxury-200">
                        <button
                          onClick={() => downloadCredentials(selectedOrder)}
                          className="w-full py-2 glass rounded-xl hover:bg-primary-50 transition-colors flex items-center justify-center space-x-2 text-sm"
                        >
                          <FiDownload className="w-4 h-4" />
                          <span className="hidden sm:inline">
                            Download as Text File
                          </span>
                          <span className="sm:hidden">Download</span>
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setShowCredentials(false)}
                    className="mt-4 w-full py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
                  >
                    Close
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};
